import { type FC, useEffect } from "react";
import { type Dispatch } from "@reduxjs/toolkit";
import { connect, type ConnectedProps } from "react-redux";
import PdfPerformanceBody from "./PdfPerformanceBody";
import { type RootState } from "../../../Application/globaltypes/redux";
import { reset } from "../state/slices/pdfPerformanceSlice";
import { bindAction } from "../../../../interfaces/redux";
import ReportingFilter from "../../../../components/reportingFilter/ReportingFilter";
import ReportingExport from "../../../../components/reportingExport/ReportingExport";
import { FeatureFlags } from "featureFlags";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { ReportUnavailable } from "components/reportUnavailable/ReportUnavailable";
import { selectIsExportActive } from "features/Reporting/state/export/exportSlice";
import { type IWizardStep } from "interfaces";

import "../../Common/utils/performanceSCSSUtils.scss";
import { useAccountFilterShouldShow } from "features/Reporting/Content/queries/useAccountFilterShouldShow";
import { usePerformanceFilter } from "features/Library/Common/Hooks/usePerformanceFilter";
import { pdfsStateSelector } from "../selectors";

export type PdfPerformanceProps = PropsFromRedux &
  IWizardStep & {
    pdfId: number;
    pdfTitle: string;
  };

export const PdfPerformance: FC<PdfPerformanceProps> = ({
  pdfId,
  pdfTitle,
  accountId,
  accountType,
  resetState,
  isExportEnabled,
  publisherId,
  acceptHandlers,
  associatedFlows,
}) => {
  const accountFilterShouldShow = useAccountFilterShouldShow(accountId, accountType, true);
  const [dateFilter, setDateFilter] = usePerformanceFilter(
    {
      includeAccountsDropdown: accountFilterShouldShow,
      includeDistinct: true,
      accountId,
      supportsSPType: true,
      includeFlows: associatedFlows !== undefined && associatedFlows.length > 0,
    },
    publisherId,
  );

  const reportEnabled = useFeatureFlag(FeatureFlags.PdfReport);

  useEffect(
    () => () => {
      resetState();
    },
    [resetState],
  );

  useEffect(() => {
    acceptHandlers?.({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!reportEnabled) {
    return <ReportUnavailable />;
  }

  return (
    <div className={"performanceRoot"}>
      <header className="performanceHeader">
        <h2 className="performanceTitle">Summary</h2>
        <div className="performanceActions">
          <ReportingFilter
            currentFilter={dateFilter}
            callback={setDateFilter}
            includeDistinct
            includeAccounts={false}
            includeAccountsDropdown={accountFilterShouldShow}
            ignoreInCountAndHide={["type"]}
            supportsSPType
            publisherId={publisherId}
            flowsData={associatedFlows}
          />
          {isExportEnabled && <ReportingExport />}
        </div>
      </header>
      <div className={"performanceRoot"}>
        <PdfPerformanceBody
          includeAccounts={accountFilterShouldShow && (dateFilter.accounts?.length ?? 0) > 0}
          pdfId={pdfId}
          pdfTitle={pdfTitle}
          pdfFilter={dateFilter}
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    isExportEnabled: selectIsExportActive(state),
    publisherId: pdfsStateSelector(state).base.details.pdf.publisherId,
    accountId: state.userProfile.accountId,
    accountType: state.userProfile.accountTypeId,
    associatedFlows: pdfsStateSelector(state).base.details.pdf.settings.flows,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  resetState: bindAction(reset, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(PdfPerformance);
export default ConnectedComponent;
